import type { DashboardAccess } from "@gjwtrack/domain";
import { useState } from "react";
import { useClient } from "../lib/client.ts";
import { Button, Checkbox, Dialog, DialogContent, DialogHeader, ListType, Panel, Select, SelectType, type SelectItem } from "../ui/ring.ts";

/** Who can view and who can edit a dashboard (YouTrack's Share dialog): everyone, groups, users. */
export function DashboardShareDialog({ access, onSave, onClose }: { access: DashboardAccess; onSave: (a: DashboardAccess) => void; onClose: () => void }) {
  const groups = useClient((s) => s.groups);
  const users = useClient((s) => s.schema?.data.users ?? []);
  const [everyone, setEveryone] = useState(access.view.everyone);
  const [view, setView] = useState({ users: access.view.users, groups: access.view.groups });
  const [edit, setEdit] = useState({ users: access.edit.users, groups: access.edit.groups });
  const data: SelectItem[] = [
    ...(groups.length ? [{ key: "t:groups", rgItemType: ListType.TITLE, label: "Groups" }] : []),
    ...groups.map((g) => ({ key: `g:${g.id}`, label: g.name })),
    { key: "t:users", rgItemType: ListType.TITLE, label: "Users" },
    ...users.map((u) => ({ key: `u:${u.id}`, label: u.name })),
  ];
  const selectedOf = (v: { users: number[]; groups: number[] }): SelectItem[] => {
    const keys = new Set([...v.groups.map((id) => `g:${id}`), ...v.users.map((id) => `u:${id}`)]);
    return data.filter((d) => keys.has(String(d.key)));
  };
  const parse = (items: SelectItem[]): { users: number[]; groups: number[] } => ({
    groups: items.filter((i) => String(i.key).startsWith("g:")).map((i) => Number(String(i.key).slice(2))),
    users: items.filter((i) => String(i.key).startsWith("u:")).map((i) => Number(String(i.key).slice(2))),
  });
  return (
    <Dialog show label="Share dashboard" onCloseAttempt={onClose} showCloseButton trapFocus autoFocusFirst={false}>
      <DialogHeader>Share dashboard</DialogHeader>
      <DialogContent>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <div className="font-semibold">Can view</div>
            <Checkbox checked={everyone} onChange={(e) => setEveryone(e.target.checked)} label="Everyone (all registered users)" />
            <Select multiple filter type={SelectType.BUTTON} label="Add groups or users…" data={data} selected={selectedOf(view)} onChange={(items: SelectItem[]) => setView(parse(items))} disabled={everyone} />
          </div>
          <div className="flex flex-col gap-2">
            <div className="font-semibold">Can edit</div>
            <div className="text-xs text-muted">Editors may rename the dashboard and change its widgets; only you can share or delete it.</div>
            <Select multiple filter type={SelectType.BUTTON} label="Add groups or users…" data={data} selected={selectedOf(edit)} onChange={(items: SelectItem[]) => setEdit(parse(items))} />
          </div>
        </div>
      </DialogContent>
      <Panel>
        <Button primary onClick={() => onSave({ view: { everyone, users: everyone ? [] : view.users, groups: everyone ? [] : view.groups }, edit })}>
          Save
        </Button>
        <Button onClick={onClose}>Cancel</Button>
      </Panel>
    </Dialog>
  );
}
